import { useNavigate } from 'react-router-dom';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';

import { PATH } from '@constants';
import { configSelector, contextNamesSelector } from '@selectors';
import { useContextParamsTracker, useSelector } from '@hooks';
import { getSlots } from '@utils';

import { Button, Icon, Link, Modal, Render, Slot, Theme } from '@components';
import { CreateProjectModal } from '@components/Modals';
import { useChat } from '@components/Providers';
import { UserPanel } from '@components/UserPanel';

import { LayoutContent } from './LayoutContent';
import { LayoutContentDrawer } from './LayoutContentDrawer';
import { LayoutOnboarding } from './LayoutOnboarding';
import { LayoutSidebar } from './LayoutSidebar';

type Props = {
  className?: string;
  /**
   * Disables layout sidebar, projects, content header
   */
  showFeatures?: boolean;
  /**
   * Based on `showFeatures` but it can be set manually
   */
  showHeaderFeatures?: boolean;
  isProjectReffered?: boolean;
  backNavigation?: boolean;
  title?: string;
  projectContextTracker?: boolean;
  clusterContextTracker?: boolean;
  organizationContextTracker?: boolean;
  children?: React.ReactNode;
};

export const Layout = ({
  className,
  showFeatures = true,
  showHeaderFeatures = showFeatures,
  isProjectReffered = true,
  backNavigation,
  title,
  projectContextTracker = true,
  clusterContextTracker = true,
  organizationContextTracker = true,
  children,
}: Props) => {
  const { projects = [] } = useSelector(configSelector);
  const { organizationName, clusterName: clusterNameContext } =
    useSelector(contextNamesSelector);

  const navigate = useNavigate();

  const {
    isOpen: isOpenChat,
    messageStatus: chatMessageStatus,
    openChat,
  } = useChat();

  useContextParamsTracker({
    project: projectContextTracker,
    cluster: clusterContextTracker,
    organization: organizationContextTracker,
  });

  const { content, ...slots } = getSlots<'header'>(children);
  const projectsExist =
    projects.filter(
      ({ orgName, clusterName }) =>
        organizationName === orgName && clusterName === clusterNameContext,
    ).length > 0;

  return (
    <div className="grid h-screen grid-cols-[minmax(0,_1fr)] grid-rows-[80px_auto] font-inter text-body">
      <div className="inset-x-0 z-30 flex items-center justify-between bg-rebecca px-8 py-5">
        <div className="mr-10 flex w-[248px] items-center justify-between">
          <Link theme aria-label="Home" to={PATH.HOME} className="py-2">
            <Theme.Logo className="h-10 w-auto max-w-[220px] text-white transition-colors" />
          </Link>
          <div className="h-12 w-px bg-white/25" />
        </div>

        <div
          className={clsx('flex min-w-0 flex-1 items-center justify-between', {
            'gap-4': !!backNavigation,
          })}
        >
          <Slot slot={slots.header}>
            <Render if={backNavigation}>
              <Button
                variant="ghost"
                className="h-auto p-0 text-[24px] text-neural-03"
                onClick={() => navigate(-1)}
              >
                <Icon icon={faChevronLeft} className="h-10 w-10" />
              </Button>
            </Render>
            <h4 className="mr-auto truncate text-h4 text-white" title={title}>
              {title}
            </h4>
          </Slot>
          <Render if={showHeaderFeatures}>
            <UserPanel />
          </Render>

          <Button
            theme
            aria-label="Open chat"
            className={clsx(
              'relative h-10 rounded-t-[20px] rounded-bl-[0px] rounded-br-[20px] bg-[rgba(255,255,255,0.18)] transition-all hover:bg-[rgba(255,255,255,0.3)]',
              {
                'ml-0 w-0 pl-0 pr-0 opacity-0 scale-0 pointer-events-none':
                  isOpenChat,
                'ml-4 w-10 pl-[9px] pr-[11px] opacity-100 scale-100 pointer-events-auto':
                  !isOpenChat,
              },
            )}
            onClick={openChat}
          >
            <Icon
              size="lg"
              color="white"
              icon={faSparkles}
              className="rotate-180"
            />

            <span
              className={clsx(
                'absolute right-0 top-0 flex h-3 w-3 items-center justify-center transition-all',
                {
                  'opacity-0 visibility-hidden': chatMessageStatus === 'empty',
                  'opacity-100 max-w-[100px]': chatMessageStatus !== 'empty',
                },
              )}
            >
              <span
                className={clsx(
                  'absolute inline-flex h-full w-full rounded-full opacity-75 [animation-duration:2s]',
                  {
                    'bg-warning animate-pulse': chatMessageStatus === 'loading',
                    'bg-error animate-ping': chatMessageStatus === 'loaded',
                  },
                )}
              />
              <span
                className={clsx(
                  'relative inline-flex h-2/3 w-2/3 rounded-full bg-error transition-opacity',
                  {
                    'opacity-0': chatMessageStatus === 'loading',
                    'opacity-100': chatMessageStatus === 'loaded',
                  },
                )}
              />
            </span>
          </Button>
        </div>
      </div>

      <div
        className={clsx(
          'relative grid min-h-0 grid-cols-[280px_minmax(0,_1fr)] bg-background',
          className,
        )}
      >
        <LayoutSidebar showFeatures={showFeatures} />

        <div className="flex min-h-0 min-w-0 flex-col overflow-auto bg-gray-100">
          {/**
           * User must have a project to use the platform so far if `isProjectReffered` is truthy
           *
           * Unlike `showFeatures` for manual disabling features for custom content
           * `projectsExists` is part of authorized layout
           */}
          <Render if={!isProjectReffered || !showFeatures || projectsExist}>
            {content}
          </Render>

          <Render if={isProjectReffered && showFeatures && !projectsExist}>
            <div className="flex w-full flex-1 flex-col items-center justify-center gap-4">
              <p className="text-center text-h5">
                Create project for
                <span className="space-both text-primary">
                  {organizationName || 'no'}
                </span>
                organization
              </p>
              <Modal content={<CreateProjectModal />}>
                <Button>Create project</Button>
              </Modal>
            </div>
          </Render>
        </div>
      </div>
    </div>
  );
};

Layout.Content = LayoutContent;
Layout.ContentDrawer = LayoutContentDrawer;
Layout.Onboarding = LayoutOnboarding;
